/**
 * Green selection ring for ?touchUi.
 *
 * With the joystick gone, nothing on screen says which wheel the next
 * drag or pinch will move. The ring is that feedback: touch a wheel and
 * it is circled in green, touch empty space and the circle goes away.
 *
 * It borrows the placement gauge's green deliberately — that colour
 * already means "this is the thing you are aiming at", and two AR
 * affordances speaking one language is worth more than a novel hue.
 */

import * as THREE from 'three'
import { GAUGE_LINE_COLOR_HEX } from '@/ar/depth-delta-visualizer'
import { SELECTION_RING_RENDER_ORDER } from '@/ar/wheel-render-order'

/** Shared with the placement gauge on purpose. */
export const SELECTION_RING_COLOR_HEX = GAUGE_LINE_COLOR_HEX

/** Ring radius as a multiple of the wheel's face radius. Far enough out
 *  to clear the tire occluder's lip (1.135) so the ring reads as a halo
 *  around the wheel rather than a stripe across the rubber. */
export const SELECTION_RING_RADIUS_RATIO = 1.22

/** Ring thickness as a multiple of face radius — constant in wheel
 *  terms, so it neither vanishes on a 17" nor overwhelms a 24". */
const SELECTION_RING_THICKNESS_RATIO = 0.045

export const SELECTION_RING_NAME = 'wheel-selection-ring'

/** Dashes, not a hoop. A solid ring read as a heavy band laid over the
 *  wheel; breaking it up keeps the circle legible as a marker while
 *  letting the wheel show through (2026-08-23). */
export const SELECTION_RING_SEGMENTS = 6

/** Angular gap between segments, in degrees. */
export const SELECTION_RING_GAP_DEG = 10

/**
 * A flat annulus in the XY plane, sized from the wheel's face radius.
 *
 * Depth testing is off: the ring lives at the wheel's own centre, so
 * with depth on it would be swallowed by the very wheel it is meant to
 * mark. It is UI drawn in world space, not geometry.
 */
export function buildSelectionRing(faceRadiusM: number): THREE.Group {
  if (!(faceRadiusM > 0) || !Number.isFinite(faceRadiusM)) {
    throw new Error(`buildSelectionRing: face radius must be positive, got ${faceRadiusM}`)
  }

  const outer = faceRadiusM * SELECTION_RING_RADIUS_RATIO
  const inner = outer - faceRadiusM * SELECTION_RING_THICKNESS_RATIO

  const step = (Math.PI * 2) / SELECTION_RING_SEGMENTS
  const gap = (SELECTION_RING_GAP_DEG * Math.PI) / 180
  const span = step - gap

  const group = new THREE.Group()
  group.name = SELECTION_RING_NAME
  // Drawn after the wheel so the no-depth-test segments land over it
  // rather than under whatever the renderer happens to sort first.
  group.renderOrder = SELECTION_RING_RENDER_ORDER

  for (let i = 0; i < SELECTION_RING_SEGMENTS; i += 1) {
    // Each segment is centred on its slot, so the gaps sit evenly
    // between them rather than all the slack collecting at one end.
    const start = i * step + gap / 2
    const geometry = new THREE.RingGeometry(inner, outer, 24, 1, start, span)
    const material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(SELECTION_RING_COLOR_HEX),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.95,
      // Always on top of its own wheel — see above.
      depthTest: false,
      depthWrite: false,
    })
    const segment = new THREE.Mesh(geometry, material)
    segment.renderOrder = SELECTION_RING_RENDER_ORDER
    group.add(segment)
  }

  return group
}

/**
 * Frees a ring's GPU resources.
 *
 * Rings are built and thrown away on every selection change, and three
 * does not reclaim geometries or materials when an object leaves the
 * graph. Tapping between wheels for a minute would otherwise leak a
 * buffer and a shader program per tap — on a phone, in an AR session
 * already close to its memory budget.
 *
 * Safe on anything: silently does nothing for a non-mesh.
 */
export function disposeSelectionRing(object: THREE.Object3D): void {
  // Walks the whole subtree: the ring is a group of segments, so
  // disposing only the root would free nothing at all.
  object.traverse((child) => {
    const mesh = child as Partial<THREE.Mesh>
    mesh.geometry?.dispose()
    const material = mesh.material
    if (Array.isArray(material)) material.forEach((m) => m.dispose())
    else material?.dispose()
  })
}
